import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { translations } from "../i18n/translations";

const AppContext = createContext(null);

// ─── Mock API layer ───────────────────────────────────────────────────────────
// Replace each function body with a real fetch() call to your backend.

const api = {
  login: async (email, password, role) => {
    // TODO: POST /api/auth/login
    await new Promise(r => setTimeout(r, 600));
    if (role === "admin") {
      return { id: "admin-1", name: "مدیر سیستم", email, role: "admin", company: "TradePortal HQ" };
    }
    return { id: "cust-1", name: "محمد احمدی", email, role: "customer", company: "AcmeCorp GmbH", country: "Germany" };
  },

  getProducts: async () => {
    // TODO: GET /api/products
    const stored = localStorage.getItem("tp_products");
    return stored ? JSON.parse(stored) : [];
  },

  saveProduct: async (product) => {
    // TODO: POST/PUT /api/products
    const stored = localStorage.getItem("tp_products");
    const list = stored ? JSON.parse(stored) : [];
    if (product.id) {
      const idx = list.findIndex(p => p.id === product.id);
      if (idx !== -1) list[idx] = product;
    } else {
      product.id = "prod-" + Date.now();
      list.push(product);
    }
    localStorage.setItem("tp_products", JSON.stringify(list));
    return product;
  },

  deleteProduct: async (id) => {
    // TODO: DELETE /api/products/:id
    const stored = localStorage.getItem("tp_products");
    const list = stored ? JSON.parse(stored) : [];
    localStorage.setItem("tp_products", JSON.stringify(list.filter(p => p.id !== id)));
  },

  getOrders: async () => {
    // TODO: GET /api/orders
    const stored = localStorage.getItem("tp_orders");
    return stored ? JSON.parse(stored) : [];
  },

  placeOrder: async (order) => {
    // TODO: POST /api/orders
    const stored = localStorage.getItem("tp_orders");
    const list = stored ? JSON.parse(stored) : [];
    const newOrder = {
      ...order,
      id: "ORD-" + Date.now(),
      status: "pending",
      createdAt: new Date().toISOString(),
    };
    list.unshift(newOrder);
    localStorage.setItem("tp_orders", JSON.stringify(list));
    return newOrder;
  },

  updateOrderStatus: async (id, status) => {
    // TODO: PATCH /api/orders/:id
    const stored = localStorage.getItem("tp_orders");
    const list = stored ? JSON.parse(stored) : [];
    const idx = list.findIndex(o => o.id === id);
    if (idx !== -1) list[idx].status = status;
    localStorage.setItem("tp_orders", JSON.stringify(list));
  },

  getCustomers: async () => {
    // TODO: GET /api/customers
    const stored = localStorage.getItem("tp_customers");
    return stored ? JSON.parse(stored) : [];
  },

  saveCustomer: async (customer) => {
    // TODO: POST/PUT /api/customers
    const stored = localStorage.getItem("tp_customers");
    const list = stored ? JSON.parse(stored) : [];
    if (customer.id) {
      const idx = list.findIndex(c => c.id === customer.id);
      if (idx !== -1) list[idx] = customer;
    } else {
      customer.id = "cust-" + Date.now();
      list.push(customer);
    }
    localStorage.setItem("tp_customers", JSON.stringify(list));
    return customer;
  },

  deleteCustomer: async (id) => {
    // TODO: DELETE /api/customers/:id
    const stored = localStorage.getItem("tp_customers");
    const list = stored ? JSON.parse(stored) : [];
    localStorage.setItem("tp_customers", JSON.stringify(list.filter(c => c.id !== id)));
  },
};

// ─── Provider ────────────────────────────────────────────────────────────────
export function AppProvider({ children }) {
  const [lang, setLang] = useState("fa");
  const [user, setUser] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(false);

  const t = translations[lang];

  const loadData = useCallback(async () => {
    const [p, o, c] = await Promise.all([api.getProducts(), api.getOrders(), api.getCustomers()]);
    setProducts(p);
    setOrders(o);
    setCustomers(c);
  }, []);

  useEffect(() => {
    if (user) loadData();
  }, [user, loadData]);

  const login = async (email, password, role) => {
    setLoading(true);
    try {
      const u = await api.login(email, password, role);
      setUser(u);
      return u;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => { setUser(null); setCart([]); };

  const saveProduct = async (product) => {
    const saved = await api.saveProduct(product);
    setProducts(prev => product.id
      ? prev.map(p => p.id === saved.id ? saved : p)
      : [...prev, saved]
    );
    return saved;
  };

  const deleteProduct = async (id) => {
    await api.deleteProduct(id);
    setProducts(prev => prev.filter(p => p.id !== id));
  };

  const placeOrder = async (orderData) => {
    setLoading(true);
    try {
      const order = await api.placeOrder({ ...orderData, customerId: user.id, customerName: user.name, company: user.company });
      setOrders(prev => [order, ...prev]);
      setCart([]);
      return order;
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (id, status) => {
    await api.updateOrderStatus(id, status);
    setOrders(prev => prev.map(o => o.id === id ? { ...o, status } : o));
  };

  const saveCustomer = async (customer) => {
    const saved = await api.saveCustomer(customer);
    setCustomers(prev => customer.id
      ? prev.map(c => c.id === saved.id ? saved : c)
      : [...prev, saved]
    );
    return saved;
  };

  const deleteCustomer = async (id) => {
    await api.deleteCustomer(id);
    setCustomers(prev => prev.filter(c => c.id !== id));
  };

  const addToCart = (product, qty) => {
    setCart(prev => {
      const existing = prev.find(i => i.product.id === product.id);
      if (existing) return prev.map(i => i.product.id === product.id ? { ...i, qty: i.qty + qty } : i);
      return [...prev, { product, qty }];
    });
  };

  const updateCartQty = (productId, qty) => {
    if (qty <= 0) setCart(prev => prev.filter(i => i.product.id !== productId));
    else setCart(prev => prev.map(i => i.product.id === productId ? { ...i, qty } : i));
  };

  const removeFromCart = (productId) => setCart(prev => prev.filter(i => i.product.id !== productId));

  const cartTotal = cart.reduce((sum, i) => sum + (i.product.price * i.qty), 0);
  const myOrders = orders.filter(o => o.customerId === user?.id);

  return (
    <AppContext.Provider value={{
      lang, setLang, t,
      user, login, logout, loading,
      products, saveProduct, deleteProduct,
      orders, myOrders, placeOrder, updateOrderStatus,
      customers, saveCustomer, deleteCustomer,
      cart, addToCart, updateCartQty, removeFromCart, cartTotal,
      reload: loadData,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
